import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertOctagon, ChevronDown, Users } from "lucide-react";

import { NotificacaoCanal, NotificacaoCanalMap, StatusEnvioNotificacao } from "@workflow/types";
import { notificacoesAdminQueries } from "@/services/api/queries/notificacoes-admin.queries";
import { PeriodoResumoNotificacao } from "@/types/notificacao-log.type";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";

import {
  CANAL_CONFIG,
  CANAL_DESCONHECIDO_CONFIG,
  CanalBadge,
  STATUS_CHART_CONFIG,
} from "./notificacao-visual";

const DESTINATARIOS_CHART_CONFIG = {
  total: { label: "Recebidas", color: "#3b82f6" },
} satisfies ChartConfig;

const LINHAS_INICIAIS = 5;

/** Barra empilhada: proporção visual dos canais no dia, sem precisar ler cada número. */
function ProporcaoCanaisBar({ porCanal, total }: { porCanal: Map<string, number>; total: number }) {
  if (total === 0) return null;

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
      {Array.from(porCanal.entries()).map(([canal, count]) => {
        const config = CANAL_CONFIG[canal as NotificacaoCanal] ?? CANAL_DESCONHECIDO_CONFIG;
        const label = NotificacaoCanalMap[canal as NotificacaoCanal] ?? "Outro";
        const percentual = Math.round((count / total) * 100);
        return (
          <Tooltip key={canal}>
            <TooltipTrigger
              render={
                <div
                  className={cn("h-full transition-opacity hover:opacity-80", config.barClassName)}
                  style={{ width: `${(count / total) * 100}%` }}
                />
              }
            />
            <TooltipContent>
              {label}: {count} ({percentual}%)
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Pílulas Enviada/Falha/Ignorada do dia + taxa de sucesso — o resumo que um auditor lê primeiro. */
function ResumoStatusDia({
  porStatus,
  total,
}: {
  porStatus: Record<string, number>;
  total: number;
}) {
  const enviadas = porStatus[StatusEnvioNotificacao.ENVIADA] ?? 0;
  const taxaSucesso = total > 0 ? Math.round((enviadas / total) * 100) : null;

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border/60 p-3">
      {Object.entries(STATUS_CHART_CONFIG).map(([status, config]) => (
        <span key={status} className="flex items-center gap-1.5 text-sm">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: config.color }} />
          <span className="font-semibold tabular-nums">{porStatus[status] ?? 0}</span>
          <span className="text-xs text-muted-foreground">{config.label}</span>
        </span>
      ))}
      {taxaSucesso !== null && (
        <span className="ml-auto text-xs font-medium text-muted-foreground">
          Taxa de sucesso: <span className="text-foreground">{taxaSucesso}%</span>
        </span>
      )}
    </div>
  );
}

/** Detalhe do dia, carregado só quando o card é expandido: status geral, motivos de falha e top destinatários. */
function DetalheDia({ dia }: { dia: string }) {
  const { data, isLoading } = useQuery(notificacoesAdminQueries.logsDetalheDia(dia));

  const { porStatus, total } = useMemo(() => {
    const porStatus: Record<string, number> = {};
    let total = 0;
    for (const row of data?.porCanalStatus ?? []) {
      porStatus[row.status] = (porStatus[row.status] ?? 0) + row.total;
      total += row.total;
    }
    return { porStatus, total };
  }, [data]);

  const destinatarios = useMemo(
    () => (data?.topDestinatarios ?? []).map((d) => ({ nome: d.nome, total: d.total })),
    [data],
  );

  if (isLoading) {
    return (
      <div className="space-y-4 pt-2">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-52 w-full rounded-lg" />
      </div>
    );
  }

  if (!data || data.porCanalStatus.length === 0) {
    return <p className="pt-2 text-xs italic text-muted-foreground">Sem detalhes para este dia.</p>;
  }

  const temErros = (data.topErros?.length ?? 0) > 0;

  return (
    <div className="space-y-4 pt-2">
      <ResumoStatusDia porStatus={porStatus} total={total} />

      <div className={cn("grid gap-4", temErros && "sm:grid-cols-2")}>
        {temErros && (
          <div className="rounded-lg border border-border/60 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              <AlertOctagon className="h-3.5 w-3.5" /> Motivos de falha
            </p>
            <ul className="space-y-1.5">
              {(data.topErros ?? []).map(({ erro, total: totalErro }) => (
                <li
                  key={erro}
                  className="flex items-start justify-between gap-3 rounded-md border border-red-500/20 bg-red-500/[0.05] px-2.5 py-1.5 text-xs"
                >
                  <span className="text-red-700 dark:text-red-300" title={erro}>
                    {erro.length > 90 ? `${erro.slice(0, 90)}…` : erro}
                  </span>
                  <span className="shrink-0 font-semibold text-red-700 dark:text-red-300">
                    {totalErro}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-lg border border-border/60 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Quem mais recebeu
          </p>
          {destinatarios.length === 0 ? (
            <p className="py-8 text-center text-xs italic text-muted-foreground">
              Nenhum destinatário.
            </p>
          ) : (
            <ChartContainer
              config={DESTINATARIOS_CHART_CONFIG}
              className="!aspect-auto h-48 w-full"
            >
              <BarChart
                data={destinatarios}
                layout="vertical"
                margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
              >
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis
                  dataKey="nome"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  width={110}
                  className="text-[11px]"
                  tickFormatter={(nome: string) =>
                    nome.length > 16 ? `${nome.slice(0, 16)}…` : nome
                  }
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="total" fill="var(--color-total)" radius={2} />
              </BarChart>
            </ChartContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export function NotificacaoLogResumoPorCanalList({ dias }: { dias: PeriodoResumoNotificacao }) {
  const [expandido, setExpandido] = useState(false);
  const [diaAberto, setDiaAberto] = useState<string | null>(null);
  const { data, isLoading } = useQuery(notificacoesAdminQueries.logsResumoPorCanal(dias));

  const diasAgrupados = useMemo(() => {
    const porDia = new Map<string, { total: number; porCanal: Map<string, number> }>();
    for (const row of data ?? []) {
      const canal = row.canal ?? "OUTRO";
      const entry = porDia.get(row.dia) ?? { total: 0, porCanal: new Map() };
      entry.total += row.total;
      entry.porCanal.set(canal, (entry.porCanal.get(canal) ?? 0) + row.total);
      porDia.set(row.dia, entry);
    }
    return Array.from(porDia.entries())
      .map(([dia, v]) => ({ dia, ...v }))
      .sort((a, b) => (a.dia < b.dia ? 1 : -1));
  }, [data]);

  const visiveis = expandido ? diasAgrupados : diasAgrupados.slice(0, LINHAS_INICIAIS);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[52px] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (diasAgrupados.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border/60 py-8 text-center text-sm italic text-muted-foreground">
        Nenhum envio no período.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {visiveis.map(({ dia, total, porCanal }) => {
        const aberto = diaAberto === dia;

        return (
          <Collapsible
            key={dia}
            open={aberto}
            onOpenChange={(open) => setDiaAberto(open ? dia : null)}
            className="rounded-xl border border-border/60 bg-card shadow-sm"
          >
            <CollapsibleTrigger className="w-full space-y-3 px-5 py-4 text-left transition-colors hover:bg-muted/20">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-base font-medium text-foreground">
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      aberto && "rotate-180",
                    )}
                  />
                  {(() => {
                    const label = format(new Date(dia), "dd/MM/yyyy · EEEE", { locale: ptBR });
                    return label.charAt(0).toUpperCase() + label.slice(1);
                  })()}
                </span>

                <div className="flex flex-wrap items-center gap-2">
                  {Array.from(porCanal.entries()).map(([canal, count]) => (
                    <CanalBadge key={canal} canal={canal} count={count} />
                  ))}

                  <span className="inline-flex items-center rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                    Total {total}
                  </span>
                </div>
              </div>

              <ProporcaoCanaisBar porCanal={porCanal} total={total} />
            </CollapsibleTrigger>

            <CollapsibleContent className="px-5 pb-4">
              {aberto && <DetalheDia dia={dia} />}
            </CollapsibleContent>
          </Collapsible>
        );
      })}

      {diasAgrupados.length > LINHAS_INICIAIS && (
        <div className="flex justify-center pt-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-3 text-xs"
            onClick={() => setExpandido((v) => !v)}
          >
            <ChevronDown
              className={cn("h-3.5 w-3.5 transition-transform", expandido && "rotate-180")}
            />
            {expandido
              ? "Mostrar menos"
              : `Mostrar mais (${diasAgrupados.length - LINHAS_INICIAIS})`}
          </Button>
        </div>
      )}
    </div>
  );
}
